<template>
  <div class="app-page__container app-page__container--no-sidebar">
    <div v-if="!isLoading">
      <h5 class="title is-5">
        {{ activeUserId === loggedInUser.id ? 'My Profile' : `${shapeUserDisplayName(activeUser)}'s Profile` }} <!-- eslint-disable-line max-len -->
      </h5>
      <div class="card">
        <div class="card-content">
          <form @submit.prevent="handleSubmitEditUserProfileForm">
            <div class="user-profile__avatar__container">
              <user-avatar :user="activeUser" size="large" />
            </div>
            <div class="field columns u-m-y-0">
              <b-field
                class="column u-p-y-0"
                :label="editUserProfileForm.firstName.label"
                :type="formErrors[editUserProfileForm.firstName.name] ? 'is-danger' : ''"
                :message="formErrors[editUserProfileForm.firstName.name] || ''"
              >
                <b-input
                  v-model="editUserProfileForm.firstName.value"
                  :disabled="isFormSubmissionInProgress || (!isLoggedInUserWorkspaceOwner && !isLoggedInUserActiveUser)"
                  @input="handleTouchForm"
                />
              </b-field>
              <b-field
                class="column u-p-y-0 u-m-b"
                :label="editUserProfileForm.lastName.label"
                :type="formErrors[editUserProfileForm.lastName.name] ? 'is-danger' : ''"
                :message="formErrors[editUserProfileForm.lastName.name] || ''"
              >
                <b-input
                  v-model="editUserProfileForm.lastName.value"
                  :disabled="isFormSubmissionInProgress || (!isLoggedInUserWorkspaceOwner && !isLoggedInUserActiveUser)"
                  @input="handleTouchForm"
                />
              </b-field>
            </div>
            <b-field
              :label="editUserProfileForm.email.label"
              :type="formErrors[editUserProfileForm.email.name] ? 'is-danger' : ''"
              :message="formErrors[editUserProfileForm.email.name] || ''"
            >
              <b-input
                v-model="editUserProfileForm.email.value"
                disabled
              />
            </b-field>
            <b-field :label="activeUserId === loggedInUser.id ? 'My Teams' : `Teams`">
              <div v-if="Object.keys(activeUserTeams).length > 0" class="card card-content">
                <div
                  v-for="team in Object.values(activeUserTeams)"
                  class="u-p-b-small"
                  :key="team.id"
                >
                  <span class="has-text-weight-bold">
                    {{ team.teamName }}
                  </span>
                  &nbsp;
                  <span class="is-size-7">
                    {{ team.userIds.length }}
                    {{ `Member${team.userIds.length > 1 ? 's' : ''}` }}
                  </span>
                </div>
              </div>
              <div v-else class="card card-content has-text-centered">
                <p class="has-text-grey-light">
                  {{ `${shapeUserDisplayName(activeUser)} has no teams.` }}
                </p>
              </div>
            </b-field>
            <b-field
              v-if="isLoggedInUserWorkspaceOwner || isLoggedInUserActiveUser"
              class="has-text-right u-p-t"
            >
              <b-button
                type="is-info"
                native-type="submit"
                :loading="isFormSubmissionInProgress"
              >
                Update Profile
              </b-button>
            </b-field>
          </form>
        </div>
      </div>
      <b-button
        v-if="!isLoggedInUserActiveUser && isLoggedInUserWorkspaceOwner"
        type="is-text"
        size="is-small"
        class="user-profile__footer__archive-profile-button has-text-danger"
        outlined
        :disabled="isFormSubmissionInProgress"
        @click.prevent="handleToggleConfirmArchiveUserModal"
      >
        Archive User
      </b-button>
      <confirm-archive-user-modal
        :activeUserId="activeUserId"
        :isModalActive="isConfirmArchiveUserModalActive"
        @close="handleToggleConfirmArchiveUserModal"
        @userArchived="handleUserArchived"
      />
    </div>
    <div v-else style="position: relative; min-height: 150px;">
      <b-loading :is-full-page="false" :active.sync="isLoading" :can-cancel="false"></b-loading>
    </div>
  </div>
</template>

<script>
import { mapState, mapGetters, mapActions } from 'vuex';
import * as UserActions from '@/store/actions/User.actions';
import * as TeamActions from '@/store/actions/Team.actions';
import * as ValidationService from '@/services/Validation.service';
import * as NotificationService from '@/services/Notification.service';
import * as notificationTypes from '@/constants/notificationTypes';
import { isObjectEmpty } from '@/helpers/dataHelpers';
import { getUserTeams } from '@/helpers/teamHelpers';
import { confirmLeaveWithUnsavedChanges } from '@/helpers/formHelpers';
import { shapeUserDisplayName } from '@/helpers/userHelpers';
import UserAvatar from '@/components/UserAvatar.vue';
import ConfirmArchiveUserModal from '@/components/ConfirmArchiveUserModal.vue';


export default {
  name: 'EditUserProfileView',

  components: {
    UserAvatar,
    ConfirmArchiveUserModal,
  },

  data() {
    return {
      activeUserId: null,
      isActiveUserLoading: true,
      areUserTeamsLoading: true,
      isConfirmArchiveUserModalActive: false,
      editUserProfileForm: {
        firstName: {
          name: 'First Name',
          label: 'First Name',
          value: '',
          required: true,
        },
        lastName: {
          name: 'Last Name',
          label: 'Last Name',
          value: '',
          required: true,
        },
        email: {
          name: 'Email',
          label: 'Email',
          value: '',
          required: true,
        },
      },
      isFormSubmissionInProgress: false,
      isFormTouched: false,
      formErrors: {},
    };
  },

  computed: {
    ...mapState({
      users: state => state.user.users,
      teams: state => state.team.teams,
    }),
    ...mapGetters([
      'activeWorkspace',
      'loggedInUser',
      'isLoggedInUserWorkspaceOwner',
    ]),
    isLoading() {
      return this.isActiveUserLoading || this.areUserTeamsLoading;
    },
    activeUser() {
      if (!this.activeUserId) return {};

      return this.users[this.activeUserId];
    },
    isActiveUserLoggedInUser() {
      if (!this.activeUserId || !this.loggedInUser.id) return false;

      return this.activeUserId === this.loggedInUser.id;
    },
    editUserProfileFormPayload() {
      return {
        userId: this.activeUserId,
        updatedUser: {
          firstName: this.editUserProfileForm.firstName.value,
          lastName: this.editUserProfileForm.lastName.value,
        },
        workspaceId: this.activeWorkspace.id,
      };
    },
    activeUserTeams() {
      return getUserTeams(this.activeUser.id, this.teams);
    },
    isLoggedInUserActiveUser() {
      return this.activeUserId === this.loggedInUser.id;
    },
  },

  created() {
    this.fetchActiveUserOnLoad();
  },

  beforeRouteLeave (to, from, next) { // eslint-disable-line
    confirmLeaveWithUnsavedChanges(this.isFormTouched, next);
  },

  watch: {
    '$route'() { // eslint-disable-line object-shorthand
      this.fetchActiveUserOnLoad();
    },
  },

  methods: {
    ...mapActions({
      fetchWorkspaceUsersByIds: UserActions.FETCH_WORKSPACE_USERS_BY_IDS,
      fetchUserTeams: TeamActions.FETCH_USER_TEAMS,
      fetchPublicUserTeams: TeamActions.FETCH_PUBLIC_USER_TEAMS,
      updateUserProfile: UserActions.UPDATE_USER_PROFILE,
    }),
    setInitialFormState() {
      this.editUserProfileForm.firstName.value = this.activeUser.firstName;
      this.editUserProfileForm.lastName.value = this.activeUser.lastName;
      this.editUserProfileForm.email.value = this.activeUser.email;
    },
    handleSubmitEditUserProfileForm() {
      this.formErrors = ValidationService.runFormValidation({ ...this.editUserProfileForm });
      if (!isObjectEmpty(this.formErrors)) return;

      this.isFormSubmissionInProgress = true;
      this.updateUserProfile(this.editUserProfileFormPayload)
        .then(() => {
          this.isFormSubmissionInProgress = false;
          this.isFormTouched = false;
          NotificationService.showNotification(notificationTypes.UPDATE_USER_PROFILE_SUCCESS);
        })
        .catch(() => {
          this.isFormSubmissionInProgress = false;
          NotificationService.showNotification(notificationTypes.UPDATE_USER_PROFILE_ERROR);
        });
    },
    handleTouchForm() {
      this.isFormTouched = true;
    },
    handleToggleConfirmArchiveUserModal() {
      this.isConfirmArchiveUserModalActive = !this.isConfirmArchiveUserModalActive;
    },
    handleUserArchived() {
      this.isFormTouched = false;

      NotificationService.showNotification(notificationTypes.ARCHIVE_USER_SUCCESS);
      this.$router.push('/users');
    },
    async fetchActiveUserOnLoad() {
      if (this.$route.path === '/my-profile') {
        this.activeUserId = this.loggedInUser.id;
      } else {
        this.activeUserId = this.$route.params.id;
      }
      try {
        const userDataPromiseArray = [
          this.fetchWorkspaceUsersByIds({ userIds: [this.activeUserId], workspaceId: this.activeWorkspace.id }),
        ];

        if (this.isActiveUserLoggedInUser) {
          userDataPromiseArray.push(this.fetchUserTeams({
            userId: this.activeUserId,
            workspaceId: this.activeWorkspace.id,
          }));
        } else {
          userDataPromiseArray.push(this.fetchPublicUserTeams({ userId: this.activeUserId, workspaceId: this.activeWorkspace.id }));
        }

        await Promise.all(userDataPromiseArray);

        this.isActiveUserLoading = false;
        this.areUserTeamsLoading = false;

        this.setInitialFormState();
      } catch (err) {
        console.error(err); // eslint-disable-line no-console
      }
    },
    shapeUserDisplayName,
  },
};
</script>

<style scoped lang="scss">
.user-profile__avatar__container {
  display: flex;
  justify-content: center;
  margin-bottom: $global-whitespace-base;
}

.user-profile__footer__archive-profile-button {
  text-decoration: none;
  margin-top: $global-whitespace-small;
}
</style>
